import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  listIapTiersApi,
  createIapTierApi,
  updateIapTierApi,
  IapTier,
} from "../../features/payments/api/payments.api"
import {
  CurrencyDollarIcon,
  PlusIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

const centsToDollarsStr = (cents: number) => (cents / 100).toFixed(2).replace(/\.00$/, "")

const emptyForm = {
  tier: "",
  priceDollars: "",
  appleProductId: "",
  googleProductId: "",
  isActive: true,
}

export const IapTiersPage = () => {
  const [items, setItems] = useState<IapTier[]>([])
  const [loading, setLoading] = useState(true)
  const [includeInactive, setIncludeInactive] = useState(false)

  const [editing, setEditing] = useState<IapTier | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listIapTiersApi(includeInactive)
      setItems([...res.data.items].sort((a, b) => a.tier - b.tier))
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setError("")
    setFormOpen(true)
  }

  const openEdit = (t: IapTier) => {
    setEditing(t)
    setForm({
      tier: String(t.tier),
      priceDollars: centsToDollarsStr(t.price_usd_cents),
      appleProductId: t.apple_product_id,
      googleProductId: t.google_product_id,
      isActive: t.is_active,
    })
    setError("")
    setFormOpen(true)
  }

  const handleSave = async () => {
    const tierNum = Number(form.tier)
    const dollars = Number(form.priceDollars)

    if (!tierNum || !Number.isFinite(dollars) || dollars <= 0 || !form.appleProductId.trim() || !form.googleProductId.trim()) {
      setError("Заполните все поля корректными значениями")
      return
    }

    setSaving(true)
    setError("")
    try {
      const price_usd_cents = Math.round(dollars * 100)
      if (editing) {
        const res = await updateIapTierApi(editing.tier, {
          price_usd_cents,
          apple_product_id: form.appleProductId.trim(),
          google_product_id: form.googleProductId.trim(),
          is_active: form.isActive,
        })
        setItems((prev) => prev.map((t) => (t.tier === editing.tier ? res.data : t)))
      } else {
        const res = await createIapTierApi({
          tier: tierNum,
          price_usd_cents,
          apple_product_id: form.appleProductId.trim(),
          google_product_id: form.googleProductId.trim(),
        })
        setItems((prev) => [...prev, res.data].sort((a, b) => a.tier - b.tier))
      }
      setFormOpen(false)
    } catch {
      setError("Не удалось сохранить уровень")
    } finally {
      setSaving(false)
    }
  }

  const visibleItems = includeInactive ? items : items.filter((t) => t.is_active)

  return (
    <DashboardLayout title="IAP-уровни цен">
      <div className="space-y-4">

        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 leading-relaxed">
            Добавляйте уровень здесь только после того, как соответствующий товар реально создан в
            <span className="font-semibold"> App Store Connect</span> и <span className="font-semibold">Play Console</span> с
            точно таким же <span className="font-mono text-xs bg-amber-100 px-1 py-0.5 rounded">product_id</span> — иначе
            проверка покупки никогда не найдёт совпадение.
          </p>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b bg-gray-50/60">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Показывать отключённые уровни</span>
            </label>

            <button
              onClick={openCreate}
              className="ml-auto inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Новый уровень
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Уровень</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Цена</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Apple product ID</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Google product ID</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                  <th className="px-5 py-3 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i}>
                      {[1, 2, 3, 4, 5, 6].map((j) => (
                        <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                      ))}
                    </tr>
                  ))
                ) : visibleItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <CurrencyDollarIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Уровней пока нет</p>
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((t) => (
                    <tr key={t.tier} onClick={() => openEdit(t)} className="hover:bg-gray-50/60 transition-colors cursor-pointer group">
                      <td className="px-5 py-3.5 font-semibold text-gray-800">${t.tier}</td>
                      <td className="px-5 py-3.5 text-gray-700">${centsToDollarsStr(t.price_usd_cents)}</td>
                      <td className="px-5 py-3.5"><span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{t.apple_product_id}</span></td>
                      <td className="px-5 py-3.5"><span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{t.google_product_id}</span></td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.is_active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-gray-100 text-gray-500 ring-1 ring-gray-200"}`}>
                          {t.is_active ? "Активен" : "Отключён"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(t) }}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                        >
                          <PencilSquareIcon className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Create/Edit drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${formOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setFormOpen(false)}
          />
          <div className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${formOpen ? "translate-x-0" : "translate-x-full"}`}>
            <div className={`bg-gradient-to-r ${editing ? "from-amber-500 to-yellow-400" : "from-blue-600 to-blue-500"} px-6 pt-6 pb-8 flex-shrink-0 relative`}>
              <button onClick={() => setFormOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <XMarkIcon className="w-4 h-4 text-white" />
              </button>

              <div className="flex items-center gap-2 mb-3">
                <CurrencyDollarIcon className="w-5 h-5 text-white/80" />
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {editing ? "Редактировать уровень" : "Новый уровень"}
                </span>
              </div>

              <p className="text-white font-semibold text-lg pr-10">
                {editing ? `Уровень $${editing.tier}` : "Добавление IAP-уровня"}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Уровень (значение из сетки Apple, $)
                </label>
                <input
                  type="number"
                  min={1}
                  disabled={!!editing}
                  value={form.tier}
                  onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
                  placeholder="например, 140"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                />
                {editing && <p className="text-[11px] text-gray-400 mt-1">Сам уровень после создания изменить нельзя.</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Цена, $</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.priceDollars}
                  onChange={(e) => setForm((f) => ({ ...f, priceDollars: e.target.value }))}
                  placeholder="139.99"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Apple product ID</label>
                <input
                  value={form.appleProductId}
                  onChange={(e) => setForm((f) => ({ ...f, appleProductId: e.target.value }))}
                  placeholder="com.vkonekte.course.tier140"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Google product ID</label>
                <input
                  value={form.googleProductId}
                  onChange={(e) => setForm((f) => ({ ...f, googleProductId: e.target.value }))}
                  placeholder="com.vkonekte.course.tier140"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {editing && (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Уровень активен (доступен для покупки)</span>
                </label>
              )}

              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>

            <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors"
              >
                Отмена
              </button>
              <button
                disabled={saving}
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
              >
                {saving
                  ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <CheckCircleIcon className="w-4 h-4" />}
                Сохранить
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}
